import { Box, Typography } from '@mui/material';
import type React from 'react';
import type { Company } from '../../types/company';
import { fillMissingBillingCycles } from '../../utils/usageUtils';
import { type Column, StandardTable } from '../common/UI/StandardTable';

interface HistoricalUsagePanelProps {
  client: Company | null;
}

const EMPTY_OBJECT: Record<string, never> = {};

export const HistoricalUsagePanel: React.FC<HistoricalUsagePanelProps> = ({ client }) => {
  if (!client) {
    return null;
  }

  let monthly = { ...(client?.usage?.monthly || EMPTY_OBJECT) };
  monthly = fillMissingBillingCycles(
    monthly,
    client?.subscription?.current_period_end as Date | string | number | undefined
  );

  const sortedCycles = Object.keys(monthly).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  const columns: Column<string>[] = [
    {
      id: 'billingCycle',
      label: 'Billing Cycle Ending',
      render: (dateKey) => (
        <>
          {new Date(dateKey).toLocaleDateString()}
          {dateKey === sortedCycles[0] && (
            <Typography variant="caption" color="primary" sx={{ ml: 1, fontWeight: 'bold' }}>
              (Current)
            </Typography>
          )}
        </>
      ),
    },
    {
      id: 'tenders',
      label: 'Tenders',
      render: (dateKey) => monthly[dateKey]?.tenders || 0,
    },
    {
      id: 'reports',
      label: 'Reports',
      render: (dateKey) => monthly[dateKey]?.reports || 0,
    },
    {
      id: 'drafts',
      label: 'Drafts',
      render: (dateKey) => monthly[dateKey]?.drafts || 0,
    },
    {
      id: 'reviews',
      label: 'Reviews',
      render: (dateKey) => monthly[dateKey]?.reviews || 0,
    },
    {
      id: 'adhoc_documents',
      label: 'Ad-Hoc Docs',
      render: (dateKey) => monthly[dateKey]?.adhoc_documents || 0,
    },
    {
      id: 'section_chats',
      label: 'AI Messages',
      render: (dateKey) => monthly[dateKey]?.section_chats || 0,
    },
  ];

  return (
    <Box sx={{ mt: 6 }}>
      <Typography variant="h6" gutterBottom color="primary">
        Historical Usage (Billing Cycles)
      </Typography>
      <StandardTable
        columns={columns}
        data={sortedCycles}
        keyExtractor={(dateKey) => dateKey}
        size="small"
      />
    </Box>
  );
};
